import { RELATIVE_DATE_BY_DSL } from '../dsl/parser';
import { RELATIVE_DATE_DSL, spellLiteral } from '../dsl/printer';
import { holeName } from '../placeholders';
import {
  FieldPath,
  FilterLiteral,
  FilterOpName,
  LiteralKind,
  LiteralRange,
  OpArgument,
  RelativeDateSpec,
  literalKind
} from '../wire';
import { FilterField, FilterVocabulary } from './vocabulary';

export interface ShorthandToken {
  raw: string;
  /** Set when the token was itself a literal (number, quantity, date…), not a bare word or string. */
  literal: FilterLiteral | null;
}

export type ShorthandValue =
  | { kind: 'list'; items: ShorthandToken[] }
  | { kind: 'range'; lo: ShorthandToken; hi: ShorthandToken };

export interface ShorthandCodec {
  op: FilterOpName;
  encode(value: ShorthandValue): OpArgument | null;
  decode(arg: OpArgument): ShorthandValue | null;
}

export interface ShorthandResolver {
  codecFor(path: FieldPath): ShorthandCodec | null;
}

function preferredOperator(field: FilterField, vocabulary: FilterVocabulary): FilterOpName {
  if (field.enumValues?.length) return 'is_any_of';
  switch (field.type) {
    case 'UID':
    case 'UID_LIST':
    case 'LIST':
      return 'is_any_of';
    case 'TEXT':
      return 'contains';
    case 'NUMBER':
    case 'INTEGER':
    case 'MASS':
    case 'LENGTH':
    case 'DURATION':
      return 'between';
    case 'DATE':
    case 'DATE_TIME':
      return vocabulary.allowsRelativeDate ? 'relative_date' : 'between';
    case 'BOOL':
      return 'equals';
    case 'TAGS':
      return 'has_any_tag';
    case 'LABELS':
      return 'label_equals';
  }
}

export function defaultOperator(
  field: FilterField,
  vocabulary: FilterVocabulary
): FilterOpName | null {
  const preferred = preferredOperator(field, vocabulary);
  return field.operators.includes(preferred) ? preferred : (field.operators[0] ?? null);
}

function literalFromToken(
  token: ShorthandToken,
  kinds: readonly LiteralKind[]
): FilterLiteral | null {
  if (token.literal) {
    const kind = literalKind(token.literal);
    if (kinds.includes(kind)) return token.literal;
    if (kind === 'integer' && kinds.includes('number')) {
      return { number: (token.literal as { integer: number }).integer };
    }
  }
  if (kinds.includes('uid')) return { uid: token.raw };
  if (kinds.includes('string')) return { string: token.raw };
  if (kinds.includes('bool') && (token.raw === 'true' || token.raw === 'false')) {
    return { bool: token.raw === 'true' };
  }
  return null;
}

function tokenFromLiteral(literal: FilterLiteral): ShorthandToken | null {
  const spelled = spellLiteral(literal);
  if (
    spelled === null ||
    literal === 'null' ||
    'list' in literal ||
    'self' in literal ||
    'uid_list' in literal
  )
    return null;
  if ('string' in literal) return { raw: literal.string, literal: null };
  if ('uid' in literal) return { raw: literal.uid, literal: null };
  if ('bool' in literal) return { raw: String(literal.bool), literal: null };
  return { raw: spelled, literal };
}

function single(value: ShorthandValue): ShorthandToken | null {
  return value.kind === 'list' && value.items.length === 1 ? value.items[0] : null;
}

function allOrNothing<T>(items: (T | null)[]): T[] | null {
  return items.length > 0 && items.every((item) => item !== null) ? (items as T[]) : null;
}

export function shorthandCodec(
  field: FilterField,
  vocabulary: FilterVocabulary
): ShorthandCodec | null {
  const op = defaultOperator(field, vocabulary);
  if (!op) return null;
  const kinds = field.literalKinds[op] ?? [];
  switch (op) {
    case 'is_any_of':
      return {
        op,
        encode: (value) =>
          value.kind === 'list'
            ? allOrNothing(value.items.map((item) => literalFromToken(item, kinds)))
            : null,
        decode: (arg) => {
          const items = allOrNothing((arg as FilterLiteral[]).map(tokenFromLiteral));
          return items && { kind: 'list', items };
        }
      };
    case 'has_any_tag':
      return {
        op,
        encode: (value) =>
          value.kind === 'list' && value.items.length > 0
            ? value.items.map((item) => item.raw)
            : null,
        decode: (arg) => ({
          kind: 'list',
          items: (arg as string[]).map((raw) => ({ raw, literal: null }))
        })
      };
    case 'contains':
      return {
        op,
        encode: (value) => single(value)?.raw ?? null,
        decode: (arg) => ({ kind: 'list', items: [{ raw: arg as string, literal: null }] })
      };
    case 'equals':
      return {
        op,
        encode: (value) => {
          const token = single(value);
          return token && literalFromToken(token, kinds);
        },
        decode: (arg) => {
          const token = tokenFromLiteral(arg as FilterLiteral);
          return token && { kind: 'list', items: [token] };
        }
      };
    case 'between':
      return {
        op,
        encode: (value) => {
          if (value.kind !== 'range') return null;
          const lo = literalFromToken(value.lo, kinds);
          const hi = literalFromToken(value.hi, kinds);
          return lo && hi ? { lo, hi } : null;
        },
        decode: (arg) => {
          const range = arg as LiteralRange;
          const lo = tokenFromLiteral(range.lo);
          const hi = tokenFromLiteral(range.hi);
          return lo && hi ? { kind: 'range', lo, hi } : null;
        }
      };
    case 'relative_date':
      return {
        op,
        encode: (value) => RELATIVE_DATE_BY_DSL.get(single(value)?.raw ?? '') ?? null,
        decode: (arg) => {
          const spec = arg as RelativeDateSpec;
          return typeof spec === 'string'
            ? { kind: 'list', items: [{ raw: RELATIVE_DATE_DSL[spec], literal: null }] }
            : null;
        }
      };
    default:
      return null;
  }
}

/**
 * The search bar's reading of `field:value`. A field with a closed value list refuses any other word,
 * which the bar then reads as free text; a value matches in any case, as its suggestions do, and is
 * sent as the list spells it. A template hole stands for a value still to come, so it is taken too.
 */
export function vocabularyShorthand(vocabulary: FilterVocabulary): ShorthandResolver {
  return {
    codecFor: (path) => {
      const field = vocabulary.field(null, path);
      const codec = field ? shorthandCodec(field, vocabulary) : null;
      return codec && field?.enumValues?.length
        ? closedCodec(codec, field.enumValues)
        : codec;
    }
  };
}

function closedCodec(codec: ShorthandCodec, values: string[]): ShorthandCodec {
  const served = new Map(values.map((value) => [value.toLowerCase(), value]));
  const spell = (token: ShorthandToken): ShorthandToken | null => {
    if (holeName(token.raw) !== null) return token;
    const value = served.get(token.raw.toLowerCase());
    return value === undefined ? null : { ...token, raw: value };
  };
  return {
    ...codec,
    encode: (value) => {
      if (value.kind === 'range') {
        const lo = spell(value.lo);
        const hi = spell(value.hi);
        return lo && hi ? codec.encode({ kind: 'range', lo, hi }) : null;
      }
      const items = allOrNothing(value.items.map(spell));
      return items && codec.encode({ kind: 'list', items });
    }
  };
}
